import { Fragment } from "react";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { useControlsSettingsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.admin.controls.navigator";
import { useAdminSettingsIndexPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.admin.settings.navigator";
import { useGraphemeFileEditorSettingsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.admin.grapheme-file-editor.navigator";
import { useGraphemeFileReviewerSettingsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.admin.grapheme-file-reviewer.navigator";
import { useReactNativeListDemoSettingsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.admin.react-native-list-demo.navigator";
import { useStorybookSettingsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.admin.storybook.navigator";
import { useMnemonicsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.mnemonics.navigator";
import {
  SettingsRow,
  SettingsRowGroup,
  SettingsRowSeparator,
} from "@-/dev.improve.haniverse.routes.nav.app.settings._components.settings-row";
import { getOrpcClient } from "@-/dev.improve.haniverse.lib.orpc";
import { toast } from "@-/com.npmjs.sonner";
import { Linking, Platform } from "@-/com.npmjs.react-native";
import Constants from "@-/com.npmjs.expo-constants";
import { AdminPrimaryNavigationDestinationsSection } from "#admin-primary-navigation-destinations-section";

// Staging and alpha (the nightly trunk build) ship through the app stores, not expo.dev: the
// latest build lives on Google Play's closed-testing track (Android) / TestFlight (iOS). So on
// those builds the "check for updates" link should open that store page rather than the
// expo.dev internal-distribution install page that dev builds use.
function getStoreTestingVariant(): "alpha" | "staging" | undefined {
  const appVariant = Constants.expoConfig?.extra?.appVariant;
  return appVariant === "staging" || appVariant === "alpha" ? appVariant : undefined;
}

// App Store Connect app ids (apps/haniverse-expo/eas.json: submit.<variant>.ios.ascAppId), used
// to deep-link straight into TestFlight. Alpha's id is filled in once its App Store Connect app
// exists; until then TestFlight opens on its app list.
const IOS_ASC_APP_IDS: Record<"alpha" | "staging", string | undefined> = {
  alpha: undefined,
  staging: "6817005735",
};

function getTestFlightUrl(): string {
  const ascAppId = IOS_ASC_APP_IDS[getStoreTestingVariant() ?? "staging"];
  return ascAppId === undefined
    ? "itms-beta://"
    : `itms-beta://beta.itunes.apple.com/v1/app/${ascAppId}`;
}

// TestFlight's own URL scheme. Opening it bare launches TestFlight on its app list;
// the `/v1/app/<ascAppId>` path opens straight onto this app's build list. Falls back
// to TestFlight's App Store page when TestFlight isn't installed, so the row never
// silently does nothing.
const TESTFLIGHT_APP_STORE_URL = "https://apps.apple.com/app/testflight/id899247664";

// Opens TestFlight, landing on this app's (or else staging's) build list when possible.
// Convenience so TestFlight doesn't need a home-screen icon of its own.
async function openTestFlight(): Promise<void> {
  const url = getTestFlightUrl();
  try {
    if (await Linking.canOpenURL(url)) {
      await Linking.openURL(url);
      return;
    }
  } catch {
    // canOpenURL can reject on a malformed/unregistered scheme; fall through to the
    // App Store page rather than leaving the tap dead.
  }
  await Linking.openURL(TESTFLIGHT_APP_STORE_URL);
}

// The "open the latest staging/alpha build" store page per platform. Both are static — no
// version number is needed:
//  • Android → the Google Play closed-testing opt-in page, which always sends testers
//    to the newest build on the internal track. (The `/apps/test/<pkg>/<versionCode>`
//    deep link can't be used: the staging AAB is `eas build --local` + `eas submit
//    --path`, so neither EAS nor the app's backend knows the Play versionCode — it's
//    just `github.run_number`, baked into the AAB at build time.)
//  • iOS → the TestFlight app (`itms-beta://`), which likewise shows the latest build.
//    There's no public testflight.apple.com/join invite link, and the ASC app id can't
//    be turned into one, so the deep link is the most direct target.
function getStoreTestingUrl(platform: "android" | "ios"): string {
  const androidPackage = Constants.expoConfig?.android?.package ?? "dev.improve.haniverse.staging";
  return platform === "android"
    ? `https://play.google.com/apps/testing/${androidPackage}`
    : getTestFlightUrl();
}

// Opens the install page for the latest build of this platform so Leon can grab it
// without leaving the app. On a staging build that's the platform's store page (Google
// Play closed testing / TestFlight), opened directly since the URL is static. On a dev
// build it asks the server (which holds EXPO_API_TOKEN) for the newest internal-
// distribution build and opens its expo.dev install page. Native only — web has no
// downloadable build, and the dev-build lookup only accepts "ios" / "android".
async function openLatestBuild(platform: "android" | "ios"): Promise<void> {
  if (getStoreTestingVariant() !== undefined) {
    await Linking.openURL(getStoreTestingUrl(platform));
    return;
  }

  const loadingToastId = toast.loading("Finding the latest development build…");
  try {
    const { pageUrl } = await getOrpcClient.builds.development.get({ platform });
    toast.dismiss(loadingToastId);
    await Linking.openURL(pageUrl);
  } catch (error) {
    toast.dismiss(loadingToastId);
    toast.error("Could not find the latest development build", {
      description: error instanceof Error ? error.message : String(error),
    });
  }
}

export function AdminSettingsSection({ showTitle = true }: { showTitle?: boolean } = {}) {
  const storybookPageNavigator = useStorybookSettingsPageNavigator();
  const controlsPageNavigator = useControlsSettingsPageNavigator();
  const mnemonicsPageNavigator = useMnemonicsPageNavigator();
  const adminSettingsPageNavigator = useAdminSettingsIndexPageNavigator();
  const graphemeFileEditorPageNavigator = useGraphemeFileEditorSettingsPageNavigator();
  const graphemeFileReviewerPageNavigator = useGraphemeFileReviewerSettingsPageNavigator();
  const reactNativeListDemoPageNavigator = useReactNativeListDemoSettingsPageNavigator();
  // Platform.OS is "ios" | "android" | "web"; the build lookup only applies to the
  // two native targets that ship a downloadable internal-distribution build.
  const nativeBuildPlatform =
    Platform.OS === "ios" || Platform.OS === "android" ? Platform.OS : null;

  return (
    <Fragment>
      <SettingsRowGroup title={showTitle ? "Admin" : undefined}>
        <SettingsRow
          icon="MdAutoStories"
          label="Storybook"
          onPress={() => storybookPageNavigator.push.mutate()}
          trailing={<Icon color="#9CA3AF" name="MdChevronRight" size={20} />}
        />
        <SettingsRowSeparator />
        <SettingsRow
          icon="MdTune"
          label="Admin settings"
          onPress={() => adminSettingsPageNavigator.push.mutate()}
          trailing={<Icon color="#9CA3AF" name="MdChevronRight" size={20} />}
        />
        <SettingsRowSeparator />
        <SettingsRow
          icon="MdRefresh"
          label="Controls"
          onPress={() => controlsPageNavigator.push.mutate()}
          trailing={<Icon color="#9CA3AF" name="MdChevronRight" size={20} />}
        />
        <SettingsRowSeparator />
        <SettingsRow
          icon="MdMicNone"
          label="Grapheme File Editor"
          onPress={() => graphemeFileEditorPageNavigator.push.mutate()}
          trailing={<Icon color="#9CA3AF" name="MdChevronRight" size={20} />}
        />
        <SettingsRowSeparator />
        <SettingsRow
          icon="MdEditDocument"
          label="Grapheme File Reviewer"
          onPress={() => graphemeFileReviewerPageNavigator.push.mutate()}
          trailing={<Icon color="#9CA3AF" name="MdChevronRight" size={20} />}
        />
        <SettingsRowSeparator />
        <SettingsRow
          icon="MdViewStream"
          label="react-native-list demo"
          onPress={() => reactNativeListDemoPageNavigator.push.mutate()}
          trailing={<Icon color="#9CA3AF" name="MdChevronRight" size={20} />}
        />
        {/* Opens the install page for the latest build of this platform so it can be
            grabbed without leaving the app: Google Play closed testing / TestFlight on a
            staging build, the expo.dev install page on a dev build. Native only — web has
            no downloadable build. */}
        {nativeBuildPlatform !== null ? (
          <>
            <SettingsRowSeparator />
            <SettingsRow
              icon="MdSystemUpdate"
              label={`Check for updates (${getStoreTestingVariant() ?? "development"})`}
              onPress={() => {
                void openLatestBuild(nativeBuildPlatform);
              }}
              trailing={<Icon color="#9CA3AF" name="MdOpenInNew" size={20} />}
            />
          </>
        ) : null}
        {/* Convenience deep link into TestFlight so it doesn't need its own home-screen
            icon. iOS only — `itms-beta://` is an iOS-only scheme. */}
        {Platform.OS === "ios" ? (
          <>
            <SettingsRowSeparator />
            <SettingsRow
              icon="MdFlightTakeoff"
              label="Open TestFlight"
              onPress={() => {
                void openTestFlight();
              }}
              trailing={<Icon color="#9CA3AF" name="MdOpenInNew" size={20} />}
            />
          </>
        ) : null}
        {/* Card-authoring progress dashboard reads the bundled card files off the filesystem, so it
            only exists on web. Hide the row elsewhere rather than route to a "web only" placeholder. */}
        {Platform.OS === "web" ? (
          <>
            <SettingsRowSeparator />
            <SettingsRow
              icon="MdInsights"
              label="Mnemonics"
              onPress={() => mnemonicsPageNavigator.push.mutate()}
              trailing={<Icon color="#9CA3AF" name="MdChevronRight" size={20} />}
            />
          </>
        ) : null}
      </SettingsRowGroup>
      <AdminPrimaryNavigationDestinationsSection />
    </Fragment>
  );
}
